import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Card, Spinner, Alert, Badge, Button, Form, InputGroup, Row, Col } from 'react-bootstrap';
import AppNavbar from './Navbar';
import TaskModal from './TaskModal';
import { supabaseDataService } from '../services/supabaseService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../services/useNavigation'; // Import the custom hook
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faEye,
    faFilter,
    faSyncAlt,
    faDownload,
    faCalendarAlt,
    faSort,
    faSortUp,
    faSortDown,
    faPlus,
    faEdit,
    faTrash,
    faClock,
    faFlag,
    faUser,
    faTags,
    faTasks
} from '@fortawesome/free-solid-svg-icons';
import { useDevice } from '../contexts/DeviceContext';
import _ from "lodash";
import TaskItem from "@/components/TaskItem.jsx";
import MobileNavbar from "@/components/MobileNavbar.jsx";

const Tasks = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [showModal, setShowModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const { isMobile, isTablet, isDesktop, windowWidth } = useDevice();
    const { user } = useAuth();
    const [activeComponent, setActiveComponent] = useState('todos');

    // Use the shared navigation hook
    const { handleNavItemClick } = useNavigation();

    // Add pagination state
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [fetchingMore, setFetchingMore] = useState(false);
    const itemsPerPage = 10;

    // Create a ref for the observer
    const observer = useRef();

    // Create a ref for the last task item element
    const lastTaskElementRef = useCallback(node => {
        // Clear any existing observer
        if (observer.current) observer.current.disconnect();

        // Don't observe if we're already loading more or there's nothing more to load
        if (fetchingMore || !hasMore) return;

        // Set up the new observer if we have a node to observe
        if (node) {
            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    loadMoreTasks();
                }
            }, {
                // Add rootMargin to trigger loading a bit earlier
                rootMargin: '100px'
            });

            observer.current.observe(node);
        }
    }, [fetchingMore, hasMore]);

    useEffect(() => {
        fetchTasks();

        // Clean up observer on component unmount
        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [statusFilter, priorityFilter, categoryFilter, sortBy, sortDirection]);

    useEffect(() => {
        // Debounced search
        const delayedSearch = setTimeout(() => {
            if (searchTerm !== undefined) {
                fetchTasks();
            }
        }, 300);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    // Remove the local handleNavItemClick function since we're using the shared one

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const filters = {
                search: searchTerm,
                status: statusFilter !== 'all' ? statusFilter : null,
                priority: priorityFilter !== 'all' ? priorityFilter : null,
                category: categoryFilter !== 'all' ? categoryFilter : null,
                sortBy,
                sortDirection
            };

            const data = await supabaseDataService.getTasks(user.id, 0, itemsPerPage - 1);
            setTasks(data);
            setPage(0);
            setHasMore(data.length === itemsPerPage);
            setError('');
        } catch (err) {
            setError('Failed to load tasks. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ... rest of your component code remains the same

    const loadMoreTasks = async () => {
        if (!hasMore || fetchingMore) return;

        try {
            setFetchingMore(true);
            const nextPage = page + 1;

            const nextFrom = nextPage * itemsPerPage;
            const nextTo = nextFrom + itemsPerPage - 1;

            const filters = {
                search: searchTerm,
                status: statusFilter !== 'all' ? statusFilter : null,
                priority: priorityFilter !== 'all' ? priorityFilter : null,
                category: categoryFilter !== 'all' ? categoryFilter : null,
                sortBy,
                sortDirection
            };

            const data = await supabaseDataService.getTasks(user.id, nextFrom, nextTo);

            if (data.length > 0) {
                setTasks(prevTasks => [...prevTasks, ...data]);
                setPage(nextPage);
                setHasMore(data.length === itemsPerPage);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading more tasks:', err);
        } finally {
            setFetchingMore(false);
        }
    };

    const handleTaskUpdate = (updatedTask) => {
        setTasks(prevTasks =>
            prevTasks.map(task =>
                task.task_id === updatedTask.task_id ? updatedTask : task
            )
        );
    };

    const handleTaskDelete = async (task) => {
        console.log('[handleTaskDelete]----->', task);
        await supabaseDataService.deleteTask(task.task_id);
        await fetchTasks();
    };

    const handleCreateTask = () => {
        setSelectedTask(null);
        setShowModal(true);
    };

    const handleEditTask = (task) => {
        setSelectedTask(task);
        setShowModal(true);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return faSort;
        return sortDirection === 'asc' ? faSortUp : faSortDown;
    };

    const getPriorityBadgeVariant = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high': return 'danger';
            case 'medium': return 'warning';
            case 'low': return 'success';
            default: return 'secondary';
        }
    };

    const getStatusBadgeVariant = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'success';
            case 'in progress': return 'primary';
            case 'pending': return 'warning';
            case 'blocked': return 'danger';
            default: return 'secondary';
        }
    };

    // Manual load more button (as a fallback)
    const handleLoadMore = () => {
        loadMoreTasks();
    };

    if (loading && tasks.length === 0) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <div className="dashboard-wrapper">
            <div className="d-none d-md-block">
                <AppNavbar/>
            </div>

            {/* Mobile Navbar */}
            <MobileNavbar
                onNavItemClick={handleNavItemClick}
                activeItem={activeComponent}
            />

            {/* Rest of your JSX remains the same */}
            <Container fluid className="mt-4">
                <Card className="mb-4 shadow-sm dashboard-tasks">
                    <Card.Body style={{overflowY: 'auto'}}>
                        {tasks.length === 0 && !loading ? (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faTasks} size="3x" className="text-muted mb-3"/>
                                <p className="text-muted">No tasks found. Create your first task!</p>
                                <Button variant="primary" onClick={handleCreateTask}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2"/>
                                    Create Task
                                </Button>
                            </div>
                        ) : (
                            <>
                                {tasks.map((task, index) => {
                                    const isLastItem = index === tasks.length - 1;

                                    return (
                                        <div
                                            key={task.task_id}
                                            ref={isLastItem && hasMore ? lastTaskElementRef : null}
                                        >
                                            <TaskItem
                                                task={task}
                                                onEdit={handleEditTask}
                                                onUpdate={handleTaskUpdate}
                                                onDelete={handleTaskDelete}
                                                getPriorityBadgeVariant={getPriorityBadgeVariant}
                                                getStatusBadgeVariant={getStatusBadgeVariant}
                                            />
                                        </div>
                                    );
                                })}
                            </>
                        )}

                        {/* Loading indicator for infinite scroll */}
                        {fetchingMore && (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" role="status">
                                    <span className="visually-hidden">Loading more...</span>
                                </Spinner>
                            </div>
                        )}

                        {/* No more items message */}
                        {!hasMore && tasks.length > 0 && (
                            <div className="text-center text-muted py-3">
                                No more tasks to load
                            </div>
                        )}

                        {/* Error message */}
                        {error && (
                            <Alert variant="danger" className="mt-3">
                                {error}
                            </Alert>
                        )}

                        {/* Manual load more button (fallback) */}
                        {hasMore && !fetchingMore && tasks.length > 0 && (
                            <div className="text-center mt-3">
                                <Button variant="outline-primary" onClick={handleLoadMore}>
                                    Load More Tasks
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>

            {/* Task Modal */}
            {showModal && (
                <TaskModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    task={selectedTask}
                    onSave={handleTaskUpdate}
                    uid={user.uid}
                />
            )}
        </div>
    );
};

export default Tasks;